<script lang="ts">
    import { derived, Writable } from "svelte/store";
    import { tweened } from "svelte/motion";
    import { cubicOut } from "svelte/easing";
    import { Button } from "attractions";
    import Fa from "svelte-fa";
    import {
        faAngleLeft,
        faAngleRight,
    } from "@fortawesome/free-solid-svg-icons";

    import type { Puzzle } from "../types";
    import { resize } from "../utils/array";
    import { purgeConstraints } from "../utils/puzzle";
    import PuzzleStepperConstraints from "./PuzzleStepperConstraints.svelte";
    import PuzzleStepperFeatures from "./PuzzleStepperFeatures.svelte";
    import PuzzleStepperHeader from "./PuzzleStepperHeader.svelte";
    import PuzzleStepperPeople from "./PuzzleStepperPeople.svelte";

    export let puzzle: Writable<Puzzle>;

    const SHIFT_CONST = 480;

    let step = 0;
    const steps = ["People", "Features", "Constraints"];

    const onSelectPrevStep = () => {
        step = Math.max(0, step - 1);
    };

    const onSelectNextStep = () => {
        step = Math.min(steps.length - 1, step + 1);
    };

    const shift = tweened(step * -SHIFT_CONST, {
        duration: 400,
        easing: cubicOut,
    });

    const peopleOpacity = tweened(1, {
        duration: 400,
        easing: cubicOut,
    });
    const peopleDisplay = derived(peopleOpacity, ($peopleOpacity) =>
        $peopleOpacity > 0.01 ? "block" : "none"
    );

    const featuresOpacity = tweened(0, {
        duration: 400,
        easing: cubicOut,
    });
    const featuresDisplay = derived(featuresOpacity, ($featuresOpacity) =>
        $featuresOpacity > 0.01 ? "block" : "none"
    );

    const constraintsOpacity = tweened(0, {
        duration: 400,
        easing: cubicOut,
    });
    const constraintsDisplay = derived(
        constraintsOpacity,
        ($constraintsOpacity) => ($constraintsOpacity > 0.01 ? "block" : "none")
    );

    $: {
        shift.set(step * -SHIFT_CONST);
        switch (step) {
            case 0:
                peopleOpacity.set(1);
                featuresOpacity.set(0);
                constraintsOpacity.set(0);
                break;
            case 1:
                peopleOpacity.set(0);
                featuresOpacity.set(1);
                constraintsOpacity.set(0);
                break;
            case 2:
                peopleOpacity.set(0);
                featuresOpacity.set(0);
                constraintsOpacity.set(1);
                break;
            default:
        }
    }

    const onPeopleChange = (nextPeople: number) => {
        for (let i = 0; i < $puzzle.feats.length; i += 1) {
            $puzzle.feats[i] = resize($puzzle.feats[i], nextPeople, "");
            $puzzle.consts = purgeConstraints($puzzle);
        }
    };
</script>

<div class="root-container">
    <PuzzleStepperHeader
        {steps}
        {step}
        onChangeStep={(nextStep) => (step = nextStep)}
    />

    <div class="main-container">
        <Button neutral class="prev-btn" on:click={onSelectPrevStep}>
            <Fa icon={faAngleLeft} />
        </Button>

        <div
            class="content-container"
            style="transform: translate({$shift}px, 0)"
        >
            <div
                style="opacity: {$peopleOpacity}; visibility: {$peopleDisplay};"
            >
                <PuzzleStepperPeople
                    people={$puzzle.feats[0].length}
                    {onPeopleChange}
                />
            </div>
            <div
                style="opacity: {$featuresOpacity}; visibility: {$featuresDisplay};"
            >
                <PuzzleStepperFeatures {puzzle} />
            </div>
            <div
                style="opacity: {$constraintsOpacity}; visibility: {$constraintsDisplay};"
            >
                <PuzzleStepperConstraints {puzzle} />
            </div>
        </div>

        <Button neutral class="next-btn" on:click={onSelectNextStep}>
            <Fa icon={faAngleRight} />
        </Button>
    </div>
</div>

<style lang="scss">
    @import "theme.scss";

    .main-container {
        display: flex;
        align-items: stretch;
    }

    .content-container {
        width: 480px;
        display: flex;
        flex-direction: row;
    }

    :global(.prev-btn) {
        z-index: 100;
    }

    :global(.next-btn) {
        z-index: 100;
    }
</style>
